import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { H3 } from '../../components';
import { LISTING_CATEGORY } from '../../constants/listing.constants';
import { withViewport } from '../../util/uiHelpers';

import css from './ListingPage.module.css';
import SectionPrice from './SectionPrice';
import { buildListingFactsLine, listingFactsConfig } from './listingFacts';

const materialAdjectives = {
  steel: 'stalen',
  wood: 'houten',
  grp: 'polyester',
  aluminium: 'aluminium',
};

const boatCategories = [
  LISTING_CATEGORY.POWER_BOATS,
  LISTING_CATEGORY.SAILING_BOATS,
  LISTING_CATEGORY.SLOEP,
  LISTING_CATEGORY.INFLATABLE_BOATS,
  LISTING_CATEGORY.JETSKI,
];

const normalizeLower = value => (value ? value.toString().toLowerCase() : '');
const capitalizeFirst = value =>
  value
    ? value
        .toString()
        .charAt(0)
        .toUpperCase() + value.toString().slice(1)
    : value;

const safeLabel = (intl, id) => {
  const label = intl.formatMessage({ id });
  return label.includes(id) ? null : label;
};

const buildBoatSubtitle = (intl, publicData) => {
  const { category, type, manufacturerOriginal, hullMaterial } = publicData || {};
  if (!type) return null;

  const boatTypeLabel = safeLabel(intl, `General.listing.type.${category}.${type}`);
  if (!boatTypeLabel) return null;

  const brand = manufacturerOriginal?.trim();
  const materialAdj =
    hullMaterial && materialAdjectives[hullMaterial]
      ? materialAdjectives[hullMaterial]
      : hullMaterial
      ? normalizeLower(safeLabel(intl, `General.listing.material.${hullMaterial}`) || '')
      : null;

  const parts = [brand, materialAdj, normalizeLower(boatTypeLabel)].filter(Boolean);
  return parts.length ? capitalizeFirst(parts.join(' ')) : null;
};

const engineTypeLabel = engineType => {
  const base =
    engineType === 'inboard' ? 'scheeps' : engineType === 'outboard' ? 'buitenboord' : '';

  if (!base) return null;
  return base;
};

const buildEngineSubtitle = (intl, publicData) => {
  const engines = publicData?.boatEngines || [];
  if (!engines.length) return null;
  const count = engines.length;

  const brand = engines.find(e => e.engineManufacturer)?.engineManufacturer;
  const firstFuel = engines.find(e => e.fuelType)?.fuelType;
  const firstType = engines.find(e => e.engineType)?.engineType;

  const engineBase = engineTypeLabel(firstType);
  if (!engineBase) return null;

  const fuelLabel = firstFuel
    ? normalizeLower(intl.formatMessage({ id: `General.listing.fuelType.${firstFuel}` }))
    : null;

  if (count >= 2) {
    const parts = [brand, fuelLabel, `${engineBase}motoren`].filter(Boolean);
    return capitalizeFirst(parts.join(' '));
  }
  const electricPrefix = firstFuel === 'electric' ? 'elektrische' : null;
  const parts = [brand, electricPrefix, `${engineBase}motor`].filter(Boolean);
  return capitalizeFirst(parts.join(' '));
};

const buildTrailerSubtitle = publicData => {
  const brand = publicData?.manufacturerOriginal?.trim();
  const braked = publicData?.braked;
  const trailerLabel =
    braked === true
      ? 'geremde boottrailer'
      : braked === false
      ? 'ongeremde boottrailer'
      : 'boottrailer';
  return capitalizeFirst([brand, trailerLabel].filter(Boolean).join(' '));
};

const buildMooringSubtitle = (intl, publicData) => {
  const { category, type, town } = publicData || {};
  if (!type) return null;
  const mooringTypeLabel = safeLabel(intl, `General.listing.type.${category}.${type}`);
  if (!mooringTypeLabel) return null;
  const base = normalizeLower(mooringTypeLabel);
  return capitalizeFirst(town ? `${base} in ${town}` : base);
};

const buildSubtitle = (intl, publicData) => {
  const category = publicData?.category;
  if (!category) return null;

  if (category === LISTING_CATEGORY.OTHER) {
    return null;
  }
  if (category === LISTING_CATEGORY.BOAT_ENGINES) {
    return buildEngineSubtitle(intl, publicData);
  }
  if (category === LISTING_CATEGORY.TRAILERS) {
    return buildTrailerSubtitle(publicData);
  }
  if (category === LISTING_CATEGORY.MOORING) {
    return buildMooringSubtitle(intl, publicData);
  }
  if (boatCategories.includes(category)) {
    return buildBoatSubtitle(intl, publicData);
  }

  return null;
};

const SectionTitlePrice = props => {
  const { listing, viewport, ssrIsMobile } = props;
  const { title: displayTitle, publicData } = listing?.attributes || {};
  const intl = useIntl();
  const isMobile = viewport?.width > 0 ? viewport.width < 768 : (ssrIsMobile ?? false);

  const subtitle = useMemo(() => buildSubtitle(intl, publicData), [intl, publicData]);
  const factsLine = useMemo(
    () =>
      buildListingFactsLine(intl, publicData, listingFactsConfig, {
        maxParts: isMobile ? 4 : undefined,
        labelSizes: !isMobile,
      }),
    [intl, publicData, isMobile]
  );

  const splitTitleAndYear = title => {
    const match = title.match(/^(.*)\b(17|18|19|20)\d{2}\b$/);
    if (!match) return [title.trim(), null];
    return [match[1].trim(), match[0].match(/\d{4}$/)[0]];
  };

  const [title] = splitTitleAndYear(displayTitle || '');

  return (
    <section className={css.sectionTitlePrice}>
      <div className={css.titleStack}>
        <div className={css.titleRow}>
          <H3 as="h1" className={css.orderPanelTitle}>
            <FormattedMessage id="ListingPage.orderTitle" values={{ title }} />
          </H3>
        </div>
        {subtitle ? <div className={css.subtitle}>{subtitle}</div> : null}
      </div>

      <SectionPrice listing={listing} />
      {factsLine ? <div className={css.titlePriceFacts}>{factsLine}</div> : null}
    </section>
  );
};

export default withViewport(SectionTitlePrice);
